import { create } from 'zustand';
import type { PanelType, ViewMode } from '../types';

interface AppState {
  activePanel: PanelType;
  /** Which primary rail group has its secondary rail open. */
  activeRailGroup: string | null;
  selectedProjectId: string | null;
  viewMode: ViewMode;
  detailItemId: string | null;
  sidebarCollapsed: boolean;
  darkMode: boolean;

  setActivePanel: (panel: PanelType) => void;
  togglePanel: (panel: PanelType) => void;
  setRailGroup: (group: string | null) => void;
  toggleRailGroup: (group: string) => void;
  setSelectedProject: (id: string | null) => void;
  setViewMode: (mode: ViewMode) => void;
  setDetailItemId: (id: string | null) => void;
  toggleSidebar: () => void;
  toggleDarkMode: () => void;
}

export const useAppStore = create<AppState>((set, get) => ({
  activePanel: null,
  // Remembered across reloads: reopening to the group you were working in is
  // less disorienting than collapsing back to icons every time.
  activeRailGroup: localStorage.getItem('railGroup') || null,
  selectedProjectId: null,
  viewMode: 'kanban',
  detailItemId: null,
  sidebarCollapsed: false,
  darkMode: localStorage.getItem('darkMode') === 'true',

  setActivePanel: (panel) => set({ activePanel: panel }),

  togglePanel: (panel) => {
    const current = get().activePanel;
    set({ activePanel: current === panel ? null : panel });
  },

  setRailGroup: (group) => {
    if (group) localStorage.setItem('railGroup', group);
    else localStorage.removeItem('railGroup');
    set({ activeRailGroup: group });
  },

  toggleRailGroup: (group) => {
    const next = get().activeRailGroup === group ? null : group;
    if (next) localStorage.setItem('railGroup', next);
    else localStorage.removeItem('railGroup');
    set({ activeRailGroup: next });
  },

  setSelectedProject: (id) => set({ selectedProjectId: id }),
  setViewMode: (mode) => set({ viewMode: mode }),
  setDetailItemId: (id) => set({ detailItemId: id }),
  toggleSidebar: () => set((s) => ({ sidebarCollapsed: !s.sidebarCollapsed })),
  toggleDarkMode: () =>
    set((s) => {
      const next = !s.darkMode;
      localStorage.setItem('darkMode', String(next));
      return { darkMode: next };
    }),
}));
